import { produce } from "immer";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { reactKeyFromIDSK } from "@examspark/common";
import {
  Button,
  CheckboxButton,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconPreviewLine,
  IconRefreshLine,
  IconSearchLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { Objective, Resource, SearchAggregation } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useModal } from "../../../../hooks/useModal";
import ResourceViewerModal from "../../../resources/ResourceViewerModal";
import { useSearchPassages } from "../hooks/useSearchPassages";

import { FormSection } from "./ExamBuilder";
import PassageSelectModal from "./PassageSelectModal";
// import PreviewResource from "../../../resources/modals/PreviewResource";

// import PassageSelectModal from "./PassageSelectModal";

const Passage = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
`;
const PassageInput = styled.div``;
const PassageMeta = styled.div`
  flex: 1;
  padding: 0.2rem 0;
`;
const PassageCount = styled.div`
  width: 6rem;
  padding: 0 1rem;
  box-sizing: border-box;
`;
const PassagePreview = styled.div`
  width: 8rem;

  span {
    color: #006699;
  }
`;

const StyledNewBadge = styled.span`
  text-transform: uppercase;
  color: #fff;
  background-color: orange;
  padding: 0.5rem;
  display: inline-block;
  border-radius: 0.5rem;
  margin-right: 1rem;
`;

const StyledPassageOptions = styled(VSpace)`
  border: solid 1px #ddd;
  background-color: #ffffec;
  border-radius: 0.5rem;
  padding: 1rem;
`;

const StyledTableHeader = styled.div`
  border-bottom: solid 1px #ddd;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-end;
  font-size: 1rem;
  text-transform: uppercase;
  line-height: 1.3rem;
  color: #333;
  gap: 3rem;
`;
const StyledColumnLabel = styled.div`
  flex: 1;
`;
const StyledColumnQty = styled.div`
  width: 6rem;
  text-align: center;
`;
const StyledColumnQtyDesired = styled.div`
  width: 8rem;
`;

const Count = styled.div`
  background-color: #006699;
  color: #fff;
  border-radius: 1rem;
  text-align: center;
  padding: 0.4rem 0.5rem;
  font-size: 1rem;
`;

export const getObjectivesForItem = (item: Resource): Objective[] => {
  const objectives: Objective[] = [];
  if (item.contextItems) {
    for (const ci of item.contextItems) {
      // @ts-expect-error alignments.items type is wrong
      const objs = ci.resource?.alignments?.items;
      if (objs) {
        for (const obj of objs) {
          if (!objectives.find((_obj) => _obj.id === obj.id)) {
            objectives.push(obj);
          }
        }
      }
    }
  }

  return objectives;
};

const NewActivityPassages = (props: { objectiveAuthorityId?: string }) => {
  const [newActivityState, dispatchNewActivity] =
    useContext(NewActivityContext);
  const [preview, setPreview] = useState<Resource>();
  const [, setIsSelectModalOpen] = useState(false);
  const pickerModal = useModal();

  const { results, fetchPassages, fetchPassageFacets, isFetching } =
    useSearchPassages();
  const [facets, setFacets] = useState<SearchAggregation[]>();
  const objectiveIds = newActivityState.objectives
    .map((obj) => obj.id)
    .join(",");

  useEffect(() => {
    fetchPassages();
    fetchPassageFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveIds, newActivityState.language]);

  useEffect(() => {
    fetchPassageFacets().then((rsp) => {
      setFacets(rsp.contextIds);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newActivityState.isIncludeAllPassageItemsEnabled,
    newActivityState.passages,
  ]);

  const resultCount = results?.length || 0;

  useEffect(() => {
    dispatchNewActivity({
      type: "SET_PASSAGES_AVAILABLE_COUNT",
      count: resultCount,
    });
  }, [dispatchNewActivity, resultCount]);

  return (
    <>
      <PassageSelectModal
        {...pickerModal.modalProps}
        objectiveAuthorityId={props.objectiveAuthorityId}
        onSubmit={(values) => {
          console.log("selected passages", values);
          // const previous = state.objectives;
          setIsSelectModalOpen(false);
          pickerModal.close();
          dispatchNewActivity({
            type: "UPDATE",
            data: produce(newActivityState, (draft) => {
              for (const passage of values) {
                if (draft.passages.find((psg) => psg.id === passage.id))
                  continue;
                draft.passages.push(passage);
              }
            }),
          });
        }}
      />
      <FormSection>
        {preview && (
          <ResourceViewerModal
            isOpen={!!preview}
            onRequestClose={() => setPreview(undefined)}
            resourceKey={{ id: preview.id, sk: preview.sk }}
            size="medium"
          />
        )}
        <VSpace>
          <Text.H2>Reading Passages</Text.H2>
          <Text.Body>
            Optional: Pull items into your activity that use a specific reading
            passage.
          </Text.Body>
          <VSpace>
            {!!newActivityState.passages.length && (
              <VSpace>
                <StyledTableHeader>
                  <StyledColumnQtyDesired>Preview</StyledColumnQtyDesired>
                  <StyledColumnQty>
                    Available
                    <br />
                    Qty
                  </StyledColumnQty>
                  <StyledColumnLabel>Reading Passage</StyledColumnLabel>
                </StyledTableHeader>
                {newActivityState.passages?.map((item, idx) => {
                  const find = facets?.find((f) => f.key === item.id);
                  const count = find?.count || "";

                  return (
                    <Passage key={reactKeyFromIDSK(item)}>
                      <PassagePreview>
                        <Button
                          title="Preview"
                          variant="outlined"
                          stretch
                          icon={<IconPreviewLine />}
                          onClick={() => {
                            setPreview(item);
                          }}
                        >
                          Preview
                        </Button>
                      </PassagePreview>
                      <PassageCount>
                        <Count>{count}</Count>
                      </PassageCount>
                      <PassageMeta>{item.name}</PassageMeta>
                      <PassageInput>
                        <Button
                          title="Remove Passage"
                          variant="outlined"
                          icon={<IconTrashLine />}
                          onClick={() => {
                            dispatchNewActivity({
                              type: "UPDATE",
                              data: produce(newActivityState, (draft) => {
                                const passage = draft.passages[idx];
                                if (!passage) return;
                                draft.passages.splice(idx, 1);
                              }),
                            });
                          }}
                        />
                      </PassageInput>
                    </Passage>
                  );
                })}
              </VSpace>
            )}
            <div style={{ marginTop: "2rem" }}>
              <VSpace size="2rem">
                <Toolbar>
                  <Button
                    title="Reload"
                    variant="outlined"
                    onClick={() => fetchPassages()}
                    icon={<IconRefreshLine />}
                  >
                    Reload
                  </Button>
                  <Button
                    title="Browse Passages"
                    variant="outlined"
                    disabled={!results?.length || isFetching}
                    isLoading={isFetching}
                    color="primary"
                    icon={<IconSearchLine />}
                    onClick={() => {
                      pickerModal.open();
                    }}
                  >
                    Select Passages{" "}
                    {!!results?.length && <>({results?.length})</>}
                  </Button>
                </Toolbar>
                {!!newActivityState.passages.length && (
                  <VSpace>
                    <StyledPassageOptions size="0">
                      <div style={{ marginBottom: "1rem" }}>
                        <StyledNewBadge>New</StyledNewBadge>Customize question
                        selection for your passages.
                      </div>
                      <CheckboxButton
                        title=""
                        checked={
                          !!newActivityState.isIncludeAllPassageItemsEnabled
                        }
                        onClick={() => {
                          dispatchNewActivity({
                            type: "TOGGLE_ALL_PASSAGE_ITEMS",
                          });
                        }}
                      >
                        Pull ALL questions from these passages into my activity.
                      </CheckboxButton>
                      <CheckboxButton
                        title=""
                        checked={
                          !!newActivityState.isIncludeOnlyPassageItemsEnabled
                        }
                        onClick={() => {
                          dispatchNewActivity({
                            type: "TOGGLE_ONLY_PASSAGE_ITEMS",
                          });
                        }}
                      >
                        Do not pull any questions outside of these passages into
                        my activity.
                      </CheckboxButton>
                    </StyledPassageOptions>
                  </VSpace>
                )}
              </VSpace>
            </div>
          </VSpace>
        </VSpace>
      </FormSection>
    </>
  );
};

export default NewActivityPassages;
